import {
  fetchPaymentRequests,
  getPaymentDetails,
  getPaymentRequests,
  useBranches,
  useUsers,
  invalidatePaymentRequests,
} from "@api";
import { readCustomer, useUser } from "@auth";
import { ServerTable, Badge, PageHeader, PageTitle } from "@components";
import { toast } from "@components/toast/Toast";
import { useTableFilters } from "@components/tables/shared";
import { useTableQuery } from "@hooks";
import { useQueryClient } from "@tanstack/react-query";
import { formatAmount, formatDateTime } from "@utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { PlusLg } from "react-bootstrap-icons";
import { BellIcon } from "@heroicons/react/24/outline";
import { paymentRequestStatusColors } from "../common";
import PaymentRequestDetails from "../displays/PaymentRequestDetails";
import PlanDetailModal from "@areas/calendar/displays/PlanDetailModal";
import RequestAPayment from "../flows/RequestAPayment";
import SendReminder from "../flows/SendReminder";
import { buildApiQuery } from "@utils";

const Requests = () => {
  const [formOpen, setFormOpen] = useState(false);
  const [paymentRequestId, setPaymentRequestId] = useState(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("requestedOn");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");

  const [requestedOnFrom, setRequestsFrom] = useState(null);
  const [requestsTo, setRequestsTo] = useState(null);

  const [reminderPaymentRequest, setReminderPaymentRequest] = useState(null);
  const [planActionId, setPlanActionId] = useState(null);
  const { data: branches = [] } = useBranches();
  const { data: users = [] } = useUsers();

  const { requestId } = useParams();
  const queryClient = useQueryClient();

  // Memoize branches and users data to prevent race conditions
  const memoizedData = useMemo(() => {
    return {
      branches: branches.map((branch) => branch.name) || [],
      users: users.map((user) => user.name) || [],
    };
  }, [branches, users]);

  const tableConfig = useMemo(
    () => ({
      key: "paymentrequests",
      search: (x, s) =>
        x.customerName.toUpperCase().includes(s.toUpperCase()) ||
        x.reference.toUpperCase().includes(s.toUpperCase()) ||
        `${x.amountRequested}`.includes(s) ||
        `${formatAmount(x.amountRequested)}`.includes(s),
      onClick: (x) => setPaymentRequestId(x.id),
      isSelectable: (x) => x.amountPaid === null && x.status !== "Cancelled",
      actions: [
        {
          label: (
            <span>
              <PlusLg className="inline" /> Request payment
            </span>
          ),
          onClick: () => setFormOpen(true),
          color: "primary",
        },
      ],
      rowActions: [
        {
          icon: BellIcon,
          onClick: (x) => onSendReminder(x),
          show: (x) => x.amountPaid === null && x.status !== "Cancelled",
          title: "Send reminder",
        },
      ],
      colCustomisation: true,
      columns: [
        {
          title: "Date requested",
          key: "requestedOn",
          id: "requestedOn",
          width: "140px",
          render: (x) => formatDateTime(x.requestedOn),
          sortFunc: (a, b) => new Date(a.requestedOn) - new Date(b.requestedOn),
          type: "date",
          from: requestedOnFrom,
          setFrom: setRequestsFrom,
          to: requestsTo,
          setTo: setRequestsTo,
        },
        {
          title: "Customer",
          key: "customerName",
          id: "customerName",
          width: "200px",
          render: (x) => x.customerName,
          sortFunc: (a, b) => a.customerName.localeCompare(b.customerName),
          type: "string",
        },
        {
          title: "Reference",
          key: "reference",
          id: "reference",
          width: "150px",
          render: (x) => x.reference,
          sortFunc: (a, b) => b.reference.localeCompare(a.reference),
          type: "string",
        },
        {
          title: "Amount",
          key: "amountRequested",
          id: "amountRequested",
          width: "170px",
          render: (x) => formatAmount(x.amountPaid || x.amountRequested),
          sortFunc: (a, b) =>
            (a.amountPaid || a.amountRequested) -
            (b.amountPaid || b.amountRequested),
          type: "number",
          filterPrefix: "£",
          right: true,
        },
        {
          title: "Invoices",
          key: "invoicesRequested",
          id: "invoicesRequested",
          width: "170px",
          render: (x) => x.invoicesRequested,
          type: "enum",
          permission: readCustomer,
          values: ["All", "Overdue", "Overdue + due this month", "Any"],
        },
        {
          title: "Branch",
          key: "branch",
          id: "branch",
          width: "120px",
          render: (x) => x.branch,
          type: "enum",
          values: memoizedData.branches,
        },
        {
          title: "Requested by",
          key: "requestedByName",
          id: "requestedByName",
          width: "120px",
          render: (x) => x.requestedByName,
          type: "enum",
          values: memoizedData.users,
        },
        {
          title: "Message sent",
          key: "messageTemplateName",
          id: "messageTemplateName",
          width: "150px",
          render: (x) =>
            x.messageTemplateName || (!x.customerId ? "Cash Sale" : null),
        },
        {
          title: "Status",
          key: "status",
          id: "status",
          width: "140px",
          render: (x) => (
            <Badge color={paymentRequestStatusColors[x.status]}>
              {x.status}
            </Badge>
          ),
          type: "enum",
          values: [
            "Created",
            "Sent",
            "Delivered",
            "Opened",
            "Reminder sent",
            "Attempt failed",
            "Processing",
            "Expired",
            "Paid",
            "Refunded",
            "Part-refund",
            "Delivery failed",
            "Cancelled",
          ],
        },
      ],
    }),
    [memoizedData, readCustomer],
  );

  const filterManager = useTableFilters("paymentrequests", tableConfig.columns);
  const { filters } = filterManager;

  const getQueryParams = useCallback(
    () => ({
      page,
      pageSize: 100,
      filters,
      sort,
      sortDirection,
      searchTerm,
    }),
    [page, filters, sort, sortDirection, searchTerm],
  );

  const query = useTableQuery({
    resource: "paymentRequests",
    fetcher: fetchPaymentRequests,
    getQueryParams,
  });

  // Reset to first page when the result-set scope changes.
  useEffect(() => {
    setPage(1);
  }, [filters, sort, sortDirection, searchTerm]);

  // Handle URL parameter for opening specific request
  useEffect(() => {
    if (requestId && query.data) {
      const foundRequest = query.data.data.find((r) => r.id === requestId);
      if (foundRequest) {
        setPaymentRequestId(requestId);
      }
    }
  }, [requestId, query.data]);

  const reloadData = () => invalidatePaymentRequests(queryClient);

  const exportData = async () => {
    const query = buildApiQuery({
      page,
      pageSize: null,
      filters,
      sort,
      sortDirection,
      searchTerm,
    });

    const response = await getPaymentRequests(query);
    return response.data;
  };

  const onSendReminder = async (x) => {
    let data = await getPaymentDetails(x.id);
    setReminderPaymentRequest(data);
  };

  const handleSort = (key) => {
    if (sort === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setSortDirection("desc");
    }
  };

  const statistics = useMemo(
    () => [
      {
        label: "All",
        value: query.data ? query.data.statistics.all : null,
      },
      {
        label: "Paid",
        value: formatAmount(
          query.data ? query.data.statistics.paid : null,
        ),
      },
      {
        label: "Unpaid",
        value: formatAmount(
          query.data ? query.data.statistics.unpaid : null,
        ),
      },
    ],
    [query.data],
  );

  return (
    <>
      <PageHeader>
        <PageTitle>Payment requests</PageTitle>
      </PageHeader>
      <ServerTable
        config={tableConfig}
        data={query.data?.data || []}
        loading={query.isLoading || query.isPlaceholderData}
        statistics={statistics}
        results={query.data?.rows || 0}
        page={page}
        setPage={setPage}
        pageSize={100}
        filterManager={filterManager}
        sort={sort}
        sortDirection={sortDirection}
        onSort={handleSort}
        searchTerm={searchTerm}
        exportData={exportData}
        onSearchTermChange={(e) => setSearchTerm(e.target.value)}
      />
      <RequestAPayment
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={reloadData}
      />
      <PaymentRequestDetails
        onClose={() => setPaymentRequestId(null)}
        paymentRequestId={paymentRequestId}
        onChange={reloadData}
        onViewPlan={setPlanActionId}
        compactActions
      />
      <SendReminder
        isOpen={!!reminderPaymentRequest}
        onClose={() => setReminderPaymentRequest(null)}
        onComplete={reloadData}
        paymentRequest={reminderPaymentRequest}
      />
      <PlanDetailModal
        actionId={planActionId}
        isOpen={!!planActionId}
        onClose={() => setPlanActionId(null)}
        elevated
      />
    </>
  );
};

export default Requests;
